import React, { useMemo, useState } from "react";
import { bosSeed } from "./data/bosSeed";

const PDBOSHelp = () => {
    const [activePersona, setActivePersona] = useState(null);
    const [openSection, setOpenSection] = useState({
        universal: true,
        survival: true,
        growth: false,
        stability: false,
    });

    const universal = bosSeed.universalCore;
    const personas = bosSeed.personas;

    const selectedPersona = useMemo(() => {
        if (!activePersona) return null;
        return personas.find((p) => p.id === activePersona);
    }, [activePersona, personas]);

    const toggleSection = (key) => {
        setOpenSection((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error("Copy failed:", err);
        }
    };

    const renderTaskList = (tasks) => {
        return (
            <div className="space-y-2">
                {tasks.map((task, idx) => (
                    <div
                        key={idx}
                        className="flex items-center justify-between text-[11px] lg:text-xs text-gray-200 hover:text-white"
                    >
                        <div className="flex-1">
                            • {task}
                        </div>
                        <button
                            onClick={() => copyToClipboard(task)}
                            className="w-6 text-gray-400 hover:text-cyan-400 transition"
                            title="Copy task"
                        >
                            📋
                        </button>
                    </div>
                ))}
            </div>
        );
    };

    const Section = ({ title, sectionKey, tasks }) => (
        <div className="border border-gray-700 rounded-lg mb-3 overflow-hidden">
            <button
                onClick={() => toggleSection(sectionKey)}
                className="w-full text-[11px] lg:text-xs text-left px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-100 font-semibold transition"
            >
                {title}
            </button>

            {openSection[sectionKey] && (
                <div className="px-3 py-1 bg-gray-900">
                    {renderTaskList(tasks)}
                </div>
            )}
        </div>
    );

    return (
        <div className="space-y-2 text-xs lg:text-sm bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
            <div className="mb-2">
                <h2 className="text-xs lg:text-sm font-bold text-cyan-200">
                    🔥 PurpleDoor BOS Help
                </h2>
                <p className="text-[11px] lg:text-xs text-gray-400">
                    Behavioral Operating System Assistant
                </p>
            </div>

            <Section
                title={universal.title}
                sectionKey="universal"
                tasks={[
                    ...universal.layers.survival,
                    ...universal.layers.growth,
                    ...universal.layers.stability,
                ]}
            />

            <div className="mb-2">
                <label className="text-[11px] lg:text-xs font-semibold text-gray-300">
                    Select Persona
                </label>

                <select
                    className="w-full mt-1 px-2 py-0.5 bg-gray-900 border border-gray-700 rounded text-gray-100 focus:outline-none focus:border-cyan-500"
                    value={activePersona || ""}
                    onChange={(e) => setActivePersona(e.target.value)}
                >
                    <option value="" className="bg-gray-900 text-[11px] lg:text-xs">
                        Universal Mode (All Users)
                    </option>

                    {personas.map((p) => (
                        <option key={p.id} value={p.id} className="bg-gray-900 text-[11px] lg:text-xs">
                            {p.title}
                        </option>
                    ))}
                </select>
            </div>

            {selectedPersona && (
                <div className="mt-1">
                    <h3 className="font-bold text-[11px] lg:text-xs mb-1 text-cyan-400">
                        {selectedPersona.title}
                    </h3>

                    <Section
                        title="🔥 Survival Layer"
                        sectionKey="survival"
                        tasks={selectedPersona.layers.survival}
                    />

                    <Section
                        title="📈 Growth Layer"
                        sectionKey="growth"
                        tasks={selectedPersona.layers.growth}
                    />

                    <Section
                        title="🧱 Stability Layer"
                        sectionKey="stability"
                        tasks={selectedPersona.layers.stability}
                    />
                </div>
            )}

            {!selectedPersona && (
                <div className="mt-1 text-[11px] lg:text-xs text-gray-300 italic">
                    Select a persona to activate behavioral task layer.
                </div>
            )}
        </div>
    );
};

export default PDBOSHelp;